/**
 * DENET — sepet çekmecesi
 * Sepet yazma işlemleri Cart AJAX API ile, görsel güncelleme Section Rendering
 * API ile yapılır: fiyat ve toplam daima sunucudan gelir, istemcide hesaplanmaz.
 */

import {
  DenetElement, define, qsa, EVENTS, emit, on, lockScroll, unlockScroll, media,
} from '@denet/core';

const SECTION_ID = 'dn-cart-drawer';

class DnCartDrawer extends DenetElement {
  mounted() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.ref('dialog'));
    if (!this.dialog) return;

    on(EVENTS.drawerOpen, (event) => {
      if (event.detail?.drawer === 'cart') this.open();
    }, { signal: this.signal });

    on(EVENTS.cartUpdated, () => this.refresh(), { signal: this.signal });

    this.ref('close')?.addEventListener('click', () => this.close(), { signal: this.signal });

    this.dialog.addEventListener('close', () => unlockScroll(), { signal: this.signal });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    }, { signal: this.signal });

    this.#bindLines();
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    lockScroll();
  }

  close() {
    this.dialog?.close();
  }

  /* ------------------------------------------------------------- satırlar */

  #bindLines() {
    for (const line of qsa('[data-line-key]', this)) {
      if (!(line instanceof HTMLElement)) continue;
      const key = line.dataset.lineKey;
      if (!key) continue;

      const input = /** @type {HTMLInputElement | null} */ (line.querySelector('[data-qty="input"]'));

      line.querySelector('[data-qty="down"]')?.addEventListener('click', () => {
        this.#change(key, Math.max(0, Number(input?.value ?? 1) - 1), line);
      }, { signal: this.signal });

      line.querySelector('[data-qty="up"]')?.addEventListener('click', () => {
        this.#change(key, Number(input?.value ?? 1) + 1, line);
      }, { signal: this.signal });

      line.querySelector('[data-qty="remove"]')?.addEventListener('click', () => {
        this.#change(key, 0, line);
      }, { signal: this.signal });

      input?.addEventListener('change', () => {
        this.#change(key, Math.max(0, Number(input.value) || 0), line);
      }, { signal: this.signal });
    }
  }

  /**
   * @param {string} key
   * @param {number} quantity
   * @param {HTMLElement} line
   */
  async #change(key, quantity, line) {
    line.dataset.busy = 'true';

    try {
      const response = await fetch(window.DenetTheme?.routes?.cartChange ?? '/cart/change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: SECTION_ID }),
      });

      if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);

      const data = await response.json();
      this.#applySections(data.sections);
      this.#updateCount(data.item_count);
      emit(EVENTS.cartUpdated, { itemCount: data.item_count, silent: true });

      /* Kaldırılan satır geri alınabilir: yanlış dokunuş mobilde sık olur. */
      if (quantity === 0 && line.dataset.variantId) {
        const geri = { id: Number(line.dataset.variantId), quantity: Number(line.dataset.quantity) || 1 };
        emit('denet:toast', {
          message: this.dataset.removed || 'Sepetten kaldırıldı',
          tone: 'ok',
          action: {
            label: this.dataset.undo || 'Geri al',
            run: async () => {
              const r = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: [geri] }),
              });
              if (r.ok) emit(EVENTS.cartUpdated, { silent: true });
              else emit(EVENTS.cartError, {});
            },
          },
        });
      }
    } catch (error) {
      line.dataset.busy = 'false';
      emit(EVENTS.cartError, { error });
    }
  }

  /** Sunucudan gelen bölüm HTML'ini yerleştirir ve olayları yeniden bağlar. */
  #applySections(sections) {
    const markup = sections?.[SECTION_ID];
    if (!markup) return;

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const next = parsed.querySelector('dn-cart-drawer');
    if (!next) return;

    const wasOpen = this.dialog?.open ?? false;

    // Diyalog düğümünü değiştirmek yerine içeriğini değiştiriyoruz; böylece
    // açık diyalog kapanmıyor ve odak kaybolmuyor.
    const nextDialog = next.querySelector('[data-ref="dialog"]');
    if (nextDialog && this.dialog) {
      this.dialog.innerHTML = nextDialog.innerHTML;
    }

    this.#rebind();
    if (wasOpen && this.dialog && !this.dialog.open) this.dialog.showModal();
  }

  #rebind() {
    this.ref('close')?.addEventListener('click', () => this.close(), { signal: this.signal });
    this.#bindLines();
  }

  /** @param {number} count */
  #updateCount(count) {
    for (const badge of qsa('[data-cart-count]')) {
      badge.textContent = String(count);
      /** @type {HTMLElement} */ (badge).hidden = count === 0;
    }
  }

  /** Sepeti sunucudan tazeler (harici bir bileşen ürün eklediğinde). */
  async refresh() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=${SECTION_ID}`);
      if (!response.ok) return;
      const markup = await response.text();
      this.#applySections({ [SECTION_ID]: markup });
    } catch {
      /* sessiz: sepet bir sonraki gezinmede zaten güncel gelir */
    }
  }
}

define('dn-cart-drawer', DnCartDrawer);

/**
 * Sepete eklerken görselin sepet ikonuna doğru uçması.
 * Tek ve kısa hareket: kullanıcı neyin nereye gittiğini görür, bekletilmez.
 * @param {HTMLImageElement} image
 */
export function flyToCart(image) {
  if (media.reducedMotion) return;

  const target = document.querySelector('[data-ref="cartTrigger"]');
  if (!target) return;

  const from = image.getBoundingClientRect();
  const to = target.getBoundingClientRect();

  const clone = /** @type {HTMLImageElement} */ (image.cloneNode(true));
  clone.className = 'dn-fly';
  clone.style.left = `${from.left}px`;
  clone.style.top = `${from.top}px`;
  document.body.appendChild(clone);

  const dx = to.left + to.width / 2 - (from.left + 36);
  const dy = to.top + to.height / 2 - (from.top + 45);

  clone
    .animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.12)`, opacity: 0.2 },
      ],
      { duration: 520, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    )
    .addEventListener('finish', () => clone.remove());
}
